import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SubmenuOption} from 'bulk-import/ColumnTypeDropdown';
import {CreateDropdownOption} from 'file-browser/CreateDropdownConstants';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {
  CreateMenuComplexPolymersQuery,
  CreateMenuComplexPolymersQueryVariables,
} from 'modbio/complex-polymers/__generated/CreateMenuComplexPolymersQuery';
import {
  ComplexPolymerSchemaConfigGraphQL,
  isOligoConjugateConfigGraphQLType,
  isOligoDuplexConfigGraphQLType,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import CreateComplexPolymerModal from 'modbio/complex-polymers/create/CreateComplexPolymerModal';
import {openImportComplexPolymersFromSpreadsheet} from 'nav/CreateDropdownActions';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import runModal from 'ui-library/runModal';
import {assertNever} from 'util/Types';

import {ComplexPolymerType} from './ComplexPolymerConstants';

export const CREATE_MENU_COMPLEX_POLYMERS_QUERY: DocumentNode = gql`
  query CreateMenuComplexPolymersQuery @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    complexPolymerSchemaConfigurations {
      ... on ComplexPolymerConfigInterface {
        id
        schema {
          id
          name
          archived
        }
        configValues {
          schemaField {
            id
            name
          }
          isRequired
        }
      }
      ... on OligoConjugateSchemaConfigNode {
        id
        oligoSchemaField {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
          }
        }
        conjugatesSchemaFields {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
          }
        }
      }
      ... on OligoDuplexSchemaConfigNode {
        id
        forwardStrandSchemaField {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
          }
        }
        reverseStrandSchemaField {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
          }
        }
        conjugatesSchemaFields {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
          }
        }
      }
    }
  }
`;

const NONE_OPTION: CreateDropdownOption = {
  name: 'No complex polymer schemas',
  value: 'complex-polymer-schema-none',
  disabled: true,
};
const LOADING: CreateDropdownOption = {
  name: 'Loading...',
  value: 'complex-polymer-schema-loading',
  disabled: true,
};
const ERROR_OPTION: CreateDropdownOption = {
  name: 'Error loading complex polymer schemas',
  value: 'complex-polymer-schema-error',
  disabled: true,
  icon: 'error',
};

function getComplexPolymerCreateDropdownOptions(
  configs: Array<ComplexPolymerSchemaConfigGraphQL>,
  complexPolymerImportOption: CreateDropdownOption,
  folderId: string | null
): Array<CreateDropdownOption> {
  const oligoConjugateConfigs = configs.filter((config) => isOligoConjugateConfigGraphQLType(config));
  let oligoConjugateOptions: Array<CreateDropdownOption> = [];
  if (oligoConjugateConfigs.length > 0) {
    oligoConjugateOptions = [
      {
        name: 'Oligo conjugate',
        value: 'complex-polymer-oligo-conjugate-header',
        isHeader: true,
      },
      ...oligoConjugateConfigs.map(
        (config): CreateDropdownOption => ({
          name: config.schema.name,
          icon: 'network-wired',
          handleClick: () => {
            runModal(CreateComplexPolymerModal, {
              schemaId: config.schema.id,
              initialFolderId: folderId,
              config,
              complexPolymerType: ComplexPolymerType.OLIGO_CONJUGATE,
            });
          },
          // This value must be unique across all dropdown values (including those from 'Entity from schema'), so
          // we append complex-polymer.
          value: `${config.schema.id}complex-polymer`,
        })
      ),
    ];
  }
  const oligoDuplexConfigs = configs.filter((config) => isOligoDuplexConfigGraphQLType(config));
  let oligoDuplexOptions: Array<CreateDropdownOption> = [];
  if (oligoDuplexConfigs.length > 0) {
    oligoDuplexOptions = [
      {
        name: 'Oligo duplex',
        value: 'complex-polymer-oligo-duplex-header',
        isHeader: true,
      },
      ...oligoDuplexConfigs.map(
        (config): CreateDropdownOption => ({
          name: config.schema.name,
          icon: 'network-wired',
          handleClick: () => {
            runModal(CreateComplexPolymerModal, {
              schemaId: config.schema.id,
              initialFolderId: folderId,
              config,
              complexPolymerType: ComplexPolymerType.OLIGO_DUPLEX,
            });
          },
          value: `${config.schema.id}complex-polymer`,
        })
      ),
    ];
  }

  return [...oligoConjugateOptions, ...oligoDuplexOptions, complexPolymerImportOption];
}

export function useComplexPolymerSchemasAsCreateDropdownOptions(
  folderId: string | null
): Array<CreateDropdownOption> {
  const result = useSkippableBenchlingQuery<CreateMenuComplexPolymersQuery>(
    CREATE_MENU_COMPLEX_POLYMERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      skip: !BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES,
    }
  );

  switch (result.type) {
    case QueryResultType.SKIPPED:
      return [];
    case QueryResultType.PENDING:
      return [LOADING];
    case QueryResultType.ERROR:
      return [ERROR_OPTION];
    case QueryResultType.SUCCESS: {
      const configs = result.data.complexPolymerSchemaConfigurations.filter(
        (config) => !config.schema.archived
      );
      const complexPolymerImportOption: CreateDropdownOption = {
        name: 'Import complex polymers',
        icon: 'import',
        handleClick: () => {
          openImportComplexPolymersFromSpreadsheet(folderId, result.data.complexPolymerSchemaConfigurations);
        },
        value: 'complex-polymer-spreadsheet-import',
      };
      return configs.length === 0
        ? [NONE_OPTION]
        : getComplexPolymerCreateDropdownOptions(configs, complexPolymerImportOption, folderId);
    }
    default:
      assertAndContinue(false, `Unexpected result type: ${assertNever(result).type}`);
      return [];
  }
}

export function useComplexPolymerSchemasAsLegacyCreateDropdownOptions(
  folderId: string | null
): Array<LegacyDropdownMenuItem> {
  return useComplexPolymerSchemasAsCreateDropdownOptions(folderId).map((option: CreateDropdownOption) => ({
    name: option.name,
    icon: option.icon,
    disabled: option.disabled,
    isHeader: option.isHeader,
    handleOnClick: option.handleClick,
  }));
}

export function isComplexPolymerSchema(
  complexPolymerSchemaConfigurations: Array<ComplexPolymerSchemaConfigGraphQL>,
  schemaId?: string
): boolean {
  return complexPolymerSchemaConfigurations.some((config) => config.schema.id === schemaId);
}

export function getComplexPolymerSchemaConfiguration(
  schemaId: string | null,
  complexPolymerSchemaConfigurations: Array<ComplexPolymerSchemaConfigGraphQL>
): ComplexPolymerSchemaConfigGraphQL | null {
  if (schemaId == null) {
    return null;
  }

  const configs = complexPolymerSchemaConfigurations.filter((config) => schemaId === config.schema.id);

  assert(configs.length <= 1, 'Expected zero or one complex polymer configuration per schema');
  return configs.length === 1 ? configs[0] : null;
}

export function getComplexPolymerSchemaImportSubmenuOptions(
  complexPolymerSchemaConfiguration: ComplexPolymerSchemaConfigGraphQL | null
): Array<SubmenuOption> {
  assert(
    complexPolymerSchemaConfiguration != null,
    'Expected complex polymer schema configuration when importing complex polymer schema entities'
  );

  return complexPolymerSchemaConfiguration.configValues.map((value) => ({
    name: value.schemaField.name,
    key: value.schemaField.id,
  }));
}

export function getComplexPolymerSchemaConfigs(
  complexPolymerSchemasResult: SkippableBenchlingQueryResult<
    CreateMenuComplexPolymersQuery,
    CreateMenuComplexPolymersQueryVariables
  >
): Array<ComplexPolymerSchemaConfigGraphQL> {
  if (complexPolymerSchemasResult.type !== QueryResultType.SUCCESS) {
    return [];
  }
  return complexPolymerSchemasResult.data.complexPolymerSchemaConfigurations;
}
